import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardHeader,
  CardMedia,
  Chip,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import { useNavigate } from "react-router-dom"

import { getFeatureDetail, IOccupantDetail } from "utils/feature"
import { useTranslation } from "providers/i18n"
import { useCallback, useMemo } from "react"
import { getIcon } from "utils/icon"
import { useVenue } from "providers/venue"
import _ from "lodash"
import { StarIcon } from "components/icons"
import { OccupantData } from "providers/venue/types"
import { IFeature } from "interfaces"
import { ComingSoonChip, NewShopChip } from "components/chips"

interface IDiningCardProps {
  data: OccupantData
  sx: SxProps
  fallbackImage?: string
  isNewShop?: boolean
  isComingSoon?: boolean
}

export const DiningCard = ({
  data,
  sx,
  fallbackImage = null,
  isNewShop = false,
  isComingSoon = false,
  ...props
}: IDiningCardProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isMultiOrdinalVenue } = useVenue()
  const {
    group,
    level,
    logo,
    featuredImage,
    name,
    isFeatured,
    boothName,
    categoryIcon,
    subCategory,
    isAd,
  } = useMemo(
    () => getFeatureDetail(data as IFeature) as IOccupantDetail,
    [data]
  )

  const handleClick = useCallback(
    () => navigate(`/maps/place/${data.id}`),
    [navigate, data?.id]
  )

  const theme = useTheme()
  const {
    root,
    chipContainer,
    highlightIconChip,
    highlightIcon,
    highlightTextChip,
    levelChip,
    newShopChip,
    comingSoonChip,
    adsChip,
    subHeader,
    avatar,
    cardHeader,
    cardActionArea,
    backgroundImage,
    avatarFallBack,
    fallbackImageIcon,
    fallbackImageIconWrapper,
  } = theme.components[`DiningCard`]?.styleOverrides || {}

  // TODO: implement`showStatusChip` config from app config
  const { showStatusChip = false } =
    theme.components[`DiningCard`]?.defaultProps || {}

  const fallbackIcon = getIcon(categoryIcon, fallbackImageIcon, {
    imageIconProps: {
      solidColorFilterId: "diningCard_cover_filter",
    },
  })

  const fallbackAvatar = getIcon(categoryIcon, avatarFallBack, {
    imageIconProps: {
      solidColorFilterId: "diningCard_avatar_filter",
    },
  })

  const starIcon = useMemo(
    () =>
      getIcon(theme?.assets?.dining?.highlight, highlightIcon) || (
        <StarIcon sx={highlightIcon} />
      ),
    [theme, highlightIcon]
  )

  const levelText = isMultiOrdinalVenue
    ? t("Level Text", { level: t(level) })
    : ""
  const groupText = t(group)
  const hasImage = featuredImage || fallbackImage

  return (
    <Card
      className={!hasImage ? "no-image" : ""}
      {...props}
      sx={deepmerge(root, sx)}
    >
      <CardActionArea onClick={handleClick} sx={cardActionArea}>
        <Box sx={chipContainer}>
          {(isFeatured || isAd) && (
            <>
              <Chip label={starIcon} size="small" sx={highlightIconChip} />
              <Chip
                label={t("Highlight")}
                size="small"
                sx={highlightTextChip}
              />
              <Chip label={t("sponsored.search_result")} sx={adsChip} />
            </>
          )}
          {level && <Chip label={t(level)} size="small" sx={levelChip} />}
        </Box>
        {showStatusChip && (
          <Box sx={chipContainer}>
            {isNewShop && !isAd && <NewShopChip sx={newShopChip} />}
            {isComingSoon && !isAd && <ComingSoonChip sx={comingSoonChip} />}
          </Box>
        )}
        {hasImage || !fallbackIcon ? (
          <CardMedia
            component="img"
            image={featuredImage || fallbackImage}
            sx={backgroundImage}
          />
        ) : (
          <Box sx={fallbackImageIconWrapper}>{fallbackIcon}</Box>
        )}
        <CardHeader
          title={t(name)}
          subheader={
            <>
              {subCategory && (
                <Typography
                  component="span"
                  className="sub-category-text"
                  sx={subHeader}
                >
                  {t(subCategory)}
                </Typography>
              )}
              {boothName && (
                <Typography
                  component="span"
                  className="booth-name-text"
                  sx={subHeader}
                >
                  {`${t("Booth")} ${t(boothName)}`}
                </Typography>
              )}
              <Typography
                className="location-text"
                component="span"
                sx={subHeader}
              >
                {_.compact([levelText, groupText]).join(" ")}
              </Typography>
            </>
          }
          avatar={
            <Avatar src={logo} sx={avatar}>
              {fallbackAvatar}
            </Avatar>
          }
          sx={cardHeader}
        />
      </CardActionArea>
    </Card>
  )
}
